import React from "react";
import { Link } from "react-router-dom";
import { ChefHat, BookOpen } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { dayNumber } from "@/lib/daily";
import { trackEvent } from "@/lib/analytics";
import { pickText } from "@/lib/recipe";
import { VideoRecipeLink } from "@/components/RecipeDialog";
import LiveFood from "@/components/LiveFood";

/** Dish of the day = the country of the day's dish on even days, its dessert on odd days. */
export function dishOfDay(country, day = dayNumber()) {
  const cuisine = country?.travel_guide?.cuisine;
  if (!cuisine) return null;
  const kind = ["dish", "dessert", "street"][day % 3];
  const food = cuisine[kind] || cuisine.dish;
  return food?.recipe ? { kind: cuisine[kind] ? kind : "dish", food } : null;
}

export default function DishOfDay({ country }) {
  const { lang, t } = useLang();
  const pick = dishOfDay(country);
  if (!pick) return null;
  const { kind, food } = pick;
  const name = pickText(food.name, lang);
  return (
    <div className="mt-2 pt-3 border-t border-stoneLine flex items-center gap-3 min-w-0" data-testid="dish-of-day">
      <LiveFood food={food} kind={kind} alt={name} className="w-14 h-14 rounded-xl border border-stoneLine shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-muted-foreground"><ChefHat className="w-3 h-3 text-ochre" /> {t(kind === "dessert" ? "dessert_of_day" : kind === "street" ? "street_of_day" : "dish_of_day")}</div>
        <div className="font-semibold text-ink text-sm leading-tight truncate" data-testid="dish-of-day-name">{name}</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Link to={`/recipe/${country.code}/${kind}?lang=${lang}`} onClick={() => trackEvent("dish_of_day_recipe", { country_code: country.code, kind })}
            className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-ink text-sand hover:bg-ochre transition-colors" data-testid="dish-of-day-recipe">
            <BookOpen className="w-3 h-3" /> {t("recipe_view")}
          </Link>
          <VideoRecipeLink food={food} code={country.code} kind={kind} className="text-[11px] px-2.5 py-1" testid="dish-of-day-video" />
        </div>
      </div>
    </div>
  );
}
